import {useEffect, useState} from 'react'
import {useIsFetching, useIsMutating, useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {NexusService} from 'global-apis/nexus-service'
import {
  getFormattedDealershipList,
  getFormattedDealershipUsers,
  IDayOfWeek,
  IDate,
  IHoliday,
} from '../../utils'
import {getAvailabilityResponse, mockClosedDatesResponse, mockGetHolidaysResponse} from 'scheduling/settings/mockData'
import {formattedDaysOfWeek} from 'scheduling/settings/blocks/TeamSettings/BusinessDaysBlock/useBusinessDaysBlock'
import {ClosedDateWithReason, Calendar, OpenDayOfWeek} from '@nxcr-org/web-api-interface/lib/dealer_pb'
import {parse} from 'date-fns'

export function useTeamSettings(
  dealership,
  setDealership
){
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState( false )

  const {data: listDealerships} = useQuery({
    queryKey: ['/listDealerships'],
    queryFn: async () => {
      const response = await NexusService.listDealerships()
      return getFormattedDealershipList(response.dealersList)
    },
  })

  useEffect(() => {
    if (listDealerships && listDealerships.length) {
      setDealership(listDealerships[0])
    }
  }, [listDealerships, setDealership])

  const {data: viewTeam} = useQuery({
    queryKey: ['/viewTeam', dealership?.id],
    queryFn: async () => {
      const response = await NexusService.viewTeam(dealership.id)
      return getFormattedDealershipUsers(response.dealershipTeamList)
    },
      enabled: !!dealership
    }
  )

  function handleCancellation() {
    queryClient.invalidateQueries({queryKey:['/dealership', dealership.id]})
    queryClient.invalidateQueries({queryKey:['/dealership', 'businessDays']})
    queryClient.invalidateQueries({queryKey:['/dealership', 'closedDays']})
    queryClient.invalidateQueries({queryKey:['/dealership', 'holidays']})
    // ...invalidate all queries here
    setIsEditing(false)
  }

  function saveChanges(): Promise<any> {
    const mutations = [
      mutateBusinessDays(),
      mutateClosedDates(),
    ]

    // perform all mutations asynchronously
    return Promise.all( mutations ).then(() => {
      setIsEditing( !isEditing )
    })
  }

  function filterFutureHolidays( holidays: IDate[] ): IDate[] {
    const currentDate = Date.now()

    return holidays.filter( holiday => {
      return holiday.date.getTime() > currentDate
    })
  }

  ////////////////////////////////////////////////
  ///////////////////// MOCK /////////////////////
  ////////////////////////////////////////////////

  // NOTE: For everything below here: endpoints not available yet in NexusService,
  // so are currently mocked with mockData.ts

  // @START MOCK BUSINESS DAYS BLOCK
  const {
    data: businessDays
  } = useQuery({
    queryKey: ['/dealership', 'businessDays'],
    queryFn: async () => {
      return handleBusinessDaysResponse(getAvailabilityResponse.openDaysOfWeekList)
    },
      enabled: !!dealership,
      placeholderData: []
    }
  )

  const {
    mutateAsync: mutateBusinessDays
  } = useMutation({mutationFn: ()=>  {
    // simulates an API PUT request that takes 1 second to complete
    return new Promise<void>(function( resolve ) {
      setTimeout(function() {
        resolve()
      }, 1000)
    })
  }})

  function setBusinessDays( updatedBusinessDays: IDayOfWeek[] ): void {
    queryClient.setQueryData(['/dealership', 'businessDays'], updatedBusinessDays )
  }

  function handleBusinessDaysResponse( businessDays: OpenDayOfWeek.AsObject[] ): IDayOfWeek[] {
    return businessDays.map( day => ({
      ...day,
      dayOfWeek: formattedDaysOfWeek.find( formattedDay => formattedDay.index === day.dayOfWeek ).dayOfWeek
    }))
  }
  // @END MOCK BUSINESS DAYS BLOCK


  // @START MOCK HOLIDAYS DAYS BLOCK
  const {
    data: closedDates
  } = useQuery({
    queryKey: ['/dealership', 'closedDates'],
    queryFn: async () => {
      return handleClosedDatesResponse(mockClosedDatesResponse.closedDatesWithReasonsList)
    },
      enabled: !!dealership,
      initialData: []
    }
  )

  const {
    data: holidays
  } = useQuery({
    queryKey: ['/dealership', 'holidays'],
    queryFn: async () => {
      return handleHolidaysResponse(mockGetHolidaysResponse.calendar)
    },
      enabled: !!dealership,
      initialData: []
    }
  )

  const {
    mutateAsync: mutateClosedDates
  } = useMutation({mutationFn: () => {
    // simulates an API PUT request that takes 1 second to complete
    return new Promise<void>(function( resolve ) {
      setTimeout(function() {
        resolve()
      }, 1000)
    })
  }})

  function setClosedDates( updatedClosedDates: IDate[] ): void {
    queryClient.setQueryData(['/dealership', 'closedDates'], updatedClosedDates )
  }

  function setHolidays( holidays: IHoliday[] ){
    queryClient.setQueryData(['/dealership', 'holidays'], {...dealership, holidays} )
  }

  function handleClosedDatesResponse( closedDates: ClosedDateWithReason.AsObject[] ): IDate[] {
    return closedDates.map( ({ reason, date }) => ({
      id: date,
      reason,
      date: parse( date, 'yyyy-MM-dd', new Date() )
    }))
  }

  function handleHolidaysResponse( calendar: Calendar.AsObject ): IDate[] {
    return filterFutureHolidays(
      calendar.datesList.map( ( date ) => ({
        id: date.datestring,
        date: parse( date.datestring, calendar.dateformat, new Date() ),
        reason: date.description
      }))
    )
  }
  // @END MOCK HOLIDAYS DAYS BLOCK

  ////////////////////////////////////////////////
  ///////////////// END MOCK /////////////////////
  ////////////////////////////////////////////////

  const isFetching = useIsFetching()
  const isMutating = useIsMutating()

  return {
    dealership,
    setDealership,
    listDealerships,
    isEditing,
    isLoading: isFetching > 0 || isMutating > 0,
    businessDays,
    closedDates,
    holidays,
    setClosedDates,
    setHolidays,
    setBusinessDays,
    saveChanges,
    setIsEditing,
    viewTeam,
    handleCancellation,
  }
}

